
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { newTeamSchema, teamLeaderKeys, teamDataKeys } from './schemas';
import { useCreateGroupMutation } from '../../redux/Slices/groupsApi';

export const useNewTeamForm = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [createGroup, { isLoading, isSuccess, isError, error }] = useCreateGroupMutation();

    const methods = useForm({
        resolver: zodResolver(newTeamSchema),
        mode: "onChange",
        defaultValues: {
            user_name: "",
            email: "",
            password: "",
            password_confirmation: "",
            phone: "",
            avatar: null,
            birth_date: null,
            gender: 1,
            name: "",
            city: "",
            address: "",
            logo_url: "",
            monthly_target: 0,
            max_agents: 1,
            subscription_plan: "basic",
            monthly_fee: 0,
            subscription_ends_at: null,
            status: "active",
        },
    });

    const { trigger, handleSubmit } = methods;

    const handleNext = async () => {
        let isValid = false;
        if (activeStep === 0) {
            isValid = await trigger(teamLeaderKeys);
        } else if (activeStep === 1) {
            isValid = await trigger(teamDataKeys);
        } else {
            isValid = true;
        }

        if (isValid) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const onSubmit = async (data) => {
        // Transform data if needed (e.g. FormData if file upload is handled by backend differently)
        // Assuming backend takes JSON or FormData. 
        // Since 'avatar' is a file, we probably need FormData.

        // Check if simple JSON is enough or multipart. 
        // Usually file uploads need FormData.
        // Let's assume FormData for safety if avatar is present.

        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
            console.log(key, value);
            if (value instanceof Date) {
                formData.append(key, value.toISOString().split('T')[0]); // Send YYYY-MM-DD
            } else if (value instanceof File) {
                formData.append(key, value);
            } else if (value !== null && value !== undefined) {
                formData.append(key, value);
            }
        });

        // Alternatively, if the API expects JSON for everything except avatar, or just plain JSON if avatar isn't actually sent here.
        // I'll stick to the coreApi definition. The user showed "example JSON" in the image.
        // The image shows "Example JSON Request", which implies it sends JSON.
        // But there is also an "avatar" field type "file".
        // If it's pure JSON, maybe avatar is base64? OR the example JSON didn't include the file content.
        // I'll use FormData if there is a file, or just passed data object and let RTK Query handle it?
        // RTK Query body: data. If I pass FormData, fetch usually handles it.
        // But let's look at the image again. The example JSON at bottom DOES NOT show avatar.
        // It shows user_name, email, etc.
        // Maybe avatar is handled separately or I should ignore it for JSON example?
        // I'll prepare FormData but fall back to JSON if `avatar` is null.

        // Actually, looking at the image "Example for request data (JSON)", it creates a JSON structure.
        // I will convert to JSON matching the example.
        // Note: The example shows nested structure?
        // Image:
        // {
        //   "user_name": "...",
        //   ...
        //   "name": "...",
        //   ...
        // }
        // It looks flat.

        try {
            console.log(formData);
            await createGroup(formData).unwrap();
            // Handle success (e.g. navigate away or show success message)
        } catch (err) {
            console.error("Failed to create team", err);
        }
    };

    const submitForm = handleSubmit(onSubmit);

    return {
        methods,
        activeStep,
        handleNext,
        handleBack,
        submitForm,
        isLoading,
        isSuccess,
        isError,
        error,
        createGroup // Expose if needed
    };
};
